/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { sortTableData } from "utils/sort";
import { DataTable } from "components/DataTable";
import { getColumnsAraclar } from "components/DataTable/columnsAraclar";
import AracContextMenu from "components/ContextMenu/AracContextMenu";
import { useUserRole } from "hooks/useUserRole";
import { useUser } from "contexts/UserContext"; 
import AracEkleModal from "components/Modals/Araclar/AracEkleModal";
import ImeiDegistirModal from "components/Modals/Araclar/ImeiDegistirModal";
import ImeiGecmisiModal from "components/Modals/Araclar/ImeiGecmisiModal";
import SimGecmisiModal from "components/Modals/Araclar/SimGecmisiModal";
import PlakaDegistirModal from "components/Modals/Araclar/PlakaDegistirModal";
import PlakaGecmisModal from "components/Modals/Araclar/PlakaGecmisModal";
import NotEkleModal from "components/Modals/Araclar/NotEkleModal";
import UetdsArizaModal from "components/Modals/Araclar/UetdsArizaModal";
import UetdsKonumBildirModal from "components/Modals/Araclar/UetdsKonumBildirModal";
import IadeAlModal from "components/Modals/Araclar/IadeAlModal";
import AksesuarIslemleriModal from "components/Modals/Araclar/AksesuarIslemleriModal";
import {
  Button,
  Box,
  HStack,
  useToast,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  Input,
} from "@chakra-ui/react";
import { CheckCircleIcon, CloseIcon } from "@chakra-ui/icons";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import { useFirmOptions } from "hooks/useFirmOptions";
import { useBayiOptions } from "hooks/useBayiOptions";
import { useColumnOrder } from "hooks/useColumnOrder";
import FirmSelectorWithFilters from "components/FirmSelectorWithFilters/FirmSelectorWithFilters";
import { FiRefreshCw, FiPlus, FiDownload } from "react-icons/fi";
import { formatFirmaName } from "utils/formatFirmaName";

export default function Araclar() {
  const location = useLocation();
  const history = useHistory();
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  
  // Role kontrolü - Tüm roller erişebilir (admin, bayi, teknik_eleman)
  // Bu sayfa zaten tüm roller için açık

  // State'den otomatik firma seçimi için ID'yi oku (Hesaplar sayfasından çift tık ile gelindiğinde)
  const autoSelectFirmaIdFromState = location.state?.autoSelectFirmaId;

  // STATE
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false); // Başlangıçta false - sadece Listele butonuna basınca true olacak
  const [excelLoading, setExcelLoading] = useState(false);
  const [selectedFirmId, setSelectedFirmId] = useState("");
  const userRole = useUserRole();
  const { userBayiTipi } = useUser();
  const {
    firms,
    loading: firmsLoading,
    error: firmsError,
  } = useFirmOptions();
  // Bayiler hook'u sadece admin ve teknik_eleman için aktif (yetkili bayi göremez)
  const shouldLoadBayiler = userRole === "admin" || userRole === "teknik_eleman";
  const {
    bayiler,
    loading: bayilerLoading,
  } = useBayiOptions(shouldLoadBayiler);
  // Uygulanan firma ID'si (Listele butonuna basıldığında güncellenir)
  const [appliedSelectedFirmId, setAppliedSelectedFirmId] = useState("");
  
  const appliedSelectedFirm = useMemo(
    () =>
      appliedSelectedFirmId && appliedSelectedFirmId !== "all"
        ? firms.find((firma) => String(firma.id) === String(appliedSelectedFirmId))
        : null,
    [firms, appliedSelectedFirmId]
  );

  // Sağ tık menü state
  const [context, setContext] = useState({ position: null, row: null });

  // Modal state
  const [isEkleOpen, setEkleOpen] = useState(false);
  
  const personelOptions = useMemo(() => ["Ahmet", "Mehmet", "Ali"], []);

  // Filtre state (kullanıcının yazdığı/seçtiği değerler)
  const [plakaFilter, setPlakaFilter] = useState("");
  const [imeiFilter, setImeiFilter] = useState("");
  const [simNoFilter, setSimNoFilter] = useState("");
  const [satisTuruFilter, setSatisTuruFilter] = useState("");
  const [uetdsFilter, setUetdsFilter] = useState("");
  const [bayiFilter, setBayiFilter] = useState("");
  
  // Uygulanan filtreler (Listele butonuna basınca bu değerler kullanılacak)
  const [appliedPlakaFilter, setAppliedPlakaFilter] = useState("");
  const [appliedImeiFilter, setAppliedImeiFilter] = useState("");
  const [appliedSimNoFilter, setAppliedSimNoFilter] = useState("");
  const [appliedSatisTuruFilter, setAppliedSatisTuruFilter] = useState("");
  const [appliedUetdsFilter, setAppliedUetdsFilter] = useState("");
  const [appliedBayiFilter, setAppliedBayiFilter] = useState("");
  
  // IMEI Modal state
  const [isImeiModalOpen, setIsImeiModalOpen] = useState(false);
  const [selectedAracForImei, setSelectedAracForImei] = useState(null);
  
  // IMEI Geçmişi Modal state
  const [isImeiGecmisiModalOpen, setIsImeiGecmisiModalOpen] = useState(false);
  const [selectedAracForGecmis, setSelectedAracForGecmis] = useState(null);

  // SIM Geçmişi Modal state
  const [isSimGecmisiModalOpen, setIsSimGecmisiModalOpen] = useState(false);
  const [selectedAracForSimGecmis, setSelectedAracForSimGecmis] = useState(null);

  // Plaka Modal state
  const [isPlakaModalOpen, setIsPlakaModalOpen] = useState(false);
  const [selectedAracForPlaka, setSelectedAracForPlaka] = useState(null);
  
  // Plaka Geçmişi Modal state
  const [isPlakaGecmisiModalOpen, setIsPlakaGecmisiModalOpen] = useState(false);
  const [selectedAracForPlakaGecmis, setSelectedAracForPlakaGecmis] = useState(null);

  // Not Ekle Modal state
  const [isNotEkleModalOpen, setIsNotEkleModalOpen] = useState(false);
  const [selectedAracForNot, setSelectedAracForNot] = useState(null);

  // İade Al Modal state
  const [isIadeAlModalOpen, setIsIadeAlModalOpen] = useState(false);
  const [selectedAracForIade, setSelectedAracForIade] = useState(null);

  const [isAksesuarModalOpen, setIsAksesuarModalOpen] = useState(false);
  const [selectedAracForAksesuar, setSelectedAracForAksesuar] = useState(null);
  const [aracUetdsAriza, setAracUetdsAriza] = useState(null);
  const [aracUetdsKonum, setAracUetdsKonum] = useState(null);

  // Montaj/abonelik başlat modal state
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [startSubModal, setStartSubModal] = useState({
    isOpen: false,
    device: null,
    abonelikTipi: "", // Seçili gelmesin, kullanıcı seçmeli
    baslangic: todayIso,
    satisTuru: "", // Seçili gelmesin, kullanıcı seçmeli
    montajPersoneli: "",
    plaka: "", // ✅ Plaka alanı eklendi
  });


  // Arama yapılıp yapılmadığını takip et
  const [hasSearched, setHasSearched] = useState(false);

  // API'den araçları çek (backend'den)
  const loadDevices = useCallback((applyFilters = false, firmIdOverride = null, bayiFilterOverride = null) => {
    const effectiveFirmId =
      firmIdOverride !== null && firmIdOverride !== undefined
        ? String(firmIdOverride)
        : String(selectedFirmId || "");

    // Eğer filtreler uygulanacaksa (Listele butonuna basınca)
    if (applyFilters) {
      // Firma seçimi özel durumu: "Hepsi" seçildiğinde (value="all") bu geçerli bir filtre olarak kabul edilir
      // Böylece tüm firmaları listeleyebiliriz
      const firmaSecildi = effectiveFirmId && effectiveFirmId !== "";
      
      // En az bir filtre seçilmiş olmalı (firma seçimi "Hepsi" olsa bile geçerli)
      const hasAnyFilter = 
        firmaSecildi || // Firma seçimi yapıldıysa (Hepsi dahil) geçerli
        (plakaFilter && plakaFilter.trim()) ||
        (imeiFilter && imeiFilter.trim()) ||
        (simNoFilter && simNoFilter.trim()) ||
        (satisTuruFilter && satisTuruFilter.trim()) ||
        (uetdsFilter && uetdsFilter.trim()) ||
        (bayiFilter && bayiFilter.trim());
      
      if (!hasAnyFilter) {
        toast({
          title: "Uyarı",
          description: "Lütfen en az bir filtre seçin.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      setAppliedPlakaFilter(plakaFilter);
      setAppliedImeiFilter(imeiFilter);
      setAppliedSimNoFilter(simNoFilter);
      setAppliedSatisTuruFilter(satisTuruFilter);
      setAppliedUetdsFilter(uetdsFilter);
      setAppliedBayiFilter(bayiFilter);
      setAppliedSelectedFirmId(effectiveFirmId); // Firma ID'sini de uygula
      setHasSearched(true); // Arama yapıldığını işaretle
    }
    
    // Firma ID'sini belirle: override varsa onu kullan, yoksa selectedFirmId'yi kullan
    const firmIdToUse = effectiveFirmId;
    
    // Firma seçimi opsiyonel - seçilmediyse tüm araçları getir
    setLoading(true);

    // Bayi filtresi için query parametresi oluştur
    // Yenile butonunda mevcut uygulanan filtreleri kullan, Listele butonunda yeni filtreleri kullan
    const bayiFilterToUse = bayiFilterOverride !== null 
      ? bayiFilterOverride 
      : (applyFilters ? bayiFilter : appliedBayiFilter);
    
    const params = new URLSearchParams();
    if (bayiFilterToUse && (userRole === "admin" || userRole === "teknik_eleman")) {
      // "ixpilot" seçildiğinde özel bir değer gönder (backend'de null olarak yorumlanacak)
      if (bayiFilterToUse === "ixpilot") {
        params.append("bayi_id", "null");
      } else {
        params.append("bayi_id", bayiFilterToUse);
      }
    }

    const textFilters = applyFilters
      ? {
          plaka: plakaFilter,
          imei: imeiFilter,
          simNo: simNoFilter,
          satisTuru: satisTuruFilter,
        }
      : {
          plaka: appliedPlakaFilter,
          imei: appliedImeiFilter,
          simNo: appliedSimNoFilter,
          satisTuru: appliedSatisTuruFilter,
        };
    if (textFilters.plaka && textFilters.plaka.trim()) {
      params.append("plaka", textFilters.plaka.trim());
    }
    if (textFilters.imei && textFilters.imei.trim()) {
      params.append("imei", textFilters.imei.trim());
    }
    if (textFilters.simNo && textFilters.simNo.trim()) {
      params.append("sim_no", textFilters.simNo.trim());
    }
    if (textFilters.satisTuru && textFilters.satisTuru.trim()) {
      params.append("satis_turu", textFilters.satisTuru.trim());
    }

    const uetdsChoice = applyFilters ? uetdsFilter : appliedUetdsFilter;
    if (uetdsChoice === "acik") {
      params.append("uetds", "true");
    } else if (uetdsChoice === "kapali") {
      params.append("uetds", "false");
    }

    if (!firmIdToUse || firmIdToUse === "all") {
      const url = `/api/devices${params.toString() ? `?${params.toString()}` : ""}`;
      api
        .get(url)
        .then((res) => res.json())
        .then((data) => setDevices(Array.isArray(data) ? data : []))
        .catch(() => setDevices([]))
        .finally(() => setLoading(false));
      return;
    }

    const url = `/api/firmalar/${firmIdToUse}/devices${params.toString() ? `?${params.toString()}` : ""}`;
    api
      .get(url)
      .then((res) => res.json())
      .then((data) => setDevices(Array.isArray(data) ? data : []))
      .catch(() => setDevices([]))
      .finally(() => setLoading(false));
  }, [
    selectedFirmId,
    plakaFilter,
    imeiFilter,
    simNoFilter,
    satisTuruFilter,
    bayiFilter,
    appliedBayiFilter,
    appliedPlakaFilter,
    appliedImeiFilter,
    appliedSimNoFilter,
    appliedSatisTuruFilter,
    appliedUetdsFilter,
    uetdsFilter,
    userRole,
    toast,
  ]);

  // userBayiTipi artık Context'ten geliyor, ayrı API çağrısına gerek yok

  // Firma seçimi kaldırıldığında listeyi temizle (otomatik yükleme yok)
  useEffect(() => {
    if (!selectedFirmId) {
      // Firma seçimi kaldırıldığında listeyi temizle
      setDevices([]);
      setLoading(false);
      setHasSearched(false);
    }
  }, [selectedFirmId]);

  // Role göre kolonları filtrele
  const columns = useMemo(() => {
    return getColumnsAraclar(userRole);
  }, [userRole]);

  const [columnOrder, setColumnOrder] = useColumnOrder(columns);


  // State'den gelen otomatik firma seçimi ve listeleme (Hesaplar sayfasından çift tık ile)
  useEffect(() => {
    if (autoSelectFirmaIdFromState && firms.length > 0 && !selectedFirmId) {
      const firmId = String(autoSelectFirmaIdFromState);
      // Firma ID'sini firms listesinde kontrol et
      const matchedFirm = firms.find((f) => String(f.id) === firmId);
      
      if (matchedFirm) {
        // Firma filtresini seç
        setSelectedFirmId(firmId);
        // Otomatik olarak listeleme yap (Listele butonu basılmış gibi)
        setTimeout(() => {
          loadDevices(true, firmId, null);
        }, 200); // State güncellemesinin tamamlanmasını bekle
      }
      
      // State'i temizle (tekrar render'da tetiklenmesin diye)
      if (location.state?.autoSelectFirmaId) {
        history.replace(location.pathname, {}); // State'i temizle
      }
    }
  }, [autoSelectFirmaIdFromState, firms, selectedFirmId, loadDevices, history, location]);

  // Abonelik durumu ikon komponenti
  const AbonelikDurumuIcon = ({ durum }) => {
    const isAktif = durum === "aktif" || durum === "Aktif" || durum === true;
    
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        {isAktif ? (
          <CheckCircleIcon 
            color="green.500" 
            boxSize={5}
            filter="drop-shadow(0 2px 4px rgba(72, 187, 120, 0.3))"
            _hover={{ 
              color: "green.600",
              transform: "scale(1.1)",
              transition: "all 0.2s ease"
            }}
          />
        ) : (
          <CloseIcon 
            color="red.500" 
            boxSize={4}
            filter="drop-shadow(0 2px 4px rgba(245, 101, 101, 0.3))"
            _hover={{ 
              color: "red.600",
              transform: "scale(1.1)",
              transition: "all 0.2s ease"
            }}
          />
        )}
      </Box>
    );
  };

  // Satış türü görünümünü formatla
  const formatSatisTuru = (value) => {
    if (!value || value === "-") return "-";
    if (value === "Satılık") return "Satış";
    if (value === "Kiralık") return "Kiralama";
    return value; // Test veya diğer değerler aynen kalır
  };

  // Veriyi filtrele, kalan gün/blokaj logic ekle
  const processedData = useMemo(() => {
    return devices.map((r) => {
      // Notları parse et - sadece son notu al
      const getLastNote = (notlarString) => {
        if (!notlarString || !notlarString.trim()) return null;
        const lines = notlarString.split('\n').filter(line => line.trim());
        if (lines.length === 0) return null;
        const lastLine = lines[lines.length - 1];
        const match = lastLine.match(/\[NOT\]\s+\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}\s+-\s+(.+)/);
        if (match) {
          return match[1].trim();
        }
        return lastLine.replace(/\[NOT\]\s+\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}\s+-\s+/, '').trim();
      };

      const lastNote = getLastNote(r.notlar);
      const maxLength = 50;
      const displayNote = lastNote 
        ? (lastNote.length > maxLength ? lastNote.substring(0, maxLength) + '...' : lastNote)
        : null;

      // Kullanım durumu belirleme mantığı
      const getKullanimDurumu = () => {
        // 1. İade alınan araçlar
        if (r.disabled === true) {
          return (
            <Text fontSize="sm" color="red.500" fontWeight="semibold">
              İade Alındı
            </Text>
          );
        }
        // 2. Test araçları
        if (r.satis_turu === "Test") {
          return (
            <Text fontSize="sm" color="orange.500" fontWeight="semibold">
              Test
            </Text>
          );
        }
        // 3. Normal kullanımda
        return (
          <Text fontSize="sm" color="green.600" fontWeight="semibold">
            Kullanımda
          </Text>
        );
      };

      return {
        ...r,
        blokajRoles: r.blokajRoles ?? false,
        _satis_tarihi_timestamp: r.satis_tarihi ? new Date(r.satis_tarihi).getTime() : null,
        satis_turu: formatSatisTuru(r.satis_turu), // Formatlanmış görünüm
        satis_turu_raw: r.satis_turu, // Ham değer (filtreleme için)
        firma_ismi: r.firma_ismi ? formatFirmaName(r.firma_ismi) : r.firma_ismi,
        kullanim_durumu: getKullanimDurumu(),
        abonelik_durumu: <AbonelikDurumuIcon durum={r.abonelik_durumu} />,
        notlar: displayNote ? (
          <Tooltip label={lastNote} placement="top" hasArrow>
            <Text fontSize="sm" color="gray.700" isTruncated maxW="200px" cursor="help">
              {displayNote}
            </Text>
          </Tooltip>
        ) : (
          <Text fontSize="sm" color="gray.400">-</Text>
        ),
        uetds:
          r.uetds === true ? (
            <CheckCircleIcon color="green.500" boxSize={5} />
          ) : r.uetds === false ? (
            <CloseIcon color="red.500" boxSize={4} />
          ) : (
            <Text fontSize="xs" color="gray.500">
              Veri yok
            </Text>
          ),
      };
    });
  }, [devices]);

  // Metin/satış türü filtreleri backend'de uygulanır (liste ile export aynı veri kümesi).
  const filteredData = useMemo(() => processedData, [processedData]);

  // Sıralı veri
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    if (sortColumn === "satis_tarihi") {
      return [...filteredData].sort((a, b) => {
        const ta = a._satis_tarihi_timestamp;
        const tb = b._satis_tarihi_timestamp;
        if (!ta && !tb) return 0;
        if (!ta) return 1;
        if (!tb) return -1;
        return sortOrder === "asc" ? ta - tb : tb - ta;
      });
    }
    return sortTableData(filteredData, sortColumn, sortOrder);
  }, [filteredData, sortColumn, sortOrder]);

  // Sıralama handler'ı
  const handleSort = col => {
    if (sortColumn === col) {
      setSortOrder(o => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(col);
      setSortOrder("asc");
    }
  };

  // Sağ tık handler
  const handleContextMenu = (e, row) => {
    e.preventDefault();
    setContext({ position: { x: e.clientX, y: e.clientY }, row });
  };

  const closeMenu = () => setContext({ position: null, row: null });

  // IMEI değişikliği modal handlers
  const handleOpenImeiDegistir = (aracData) => {
    setSelectedAracForImei(aracData);
    setIsImeiModalOpen(true);
    closeMenu();
  };

  const handleImeiSuccess = (result) => {
    loadDevices(); // Liste yenile
    setIsImeiModalOpen(false);
    setSelectedAracForImei(null);
  };

  const handleStartSubSave = async () => {
    const devId = startSubModal.device?.id;
    if (!devId) return;
    if (!startSubModal.abonelikTipi || !startSubModal.baslangic || !startSubModal.satisTuru) {
      toast({
        title: "Eksik bilgi",
        description: "Abonelik tipi, başlangıç tarihi ve satış türünü seçin.",
        status: "warning",
      });
      return;
    }
    try {
      const res = await api.post(
        `/api/devices/${devId}/abonelik-baslat`,
        {
          abonelik_tipi: startSubModal.abonelikTipi,
          baslangic_tarihi: startSubModal.baslangic,
          // Backend'e raw değer gönder: "Satılık" veya "Kiralık" (validation zaten kontrol ediyor)
          satis_turu: startSubModal.satisTuru,
          montaj_personeli: startSubModal.montajPersoneli || undefined,
          plaka: startSubModal.plaka?.trim() || undefined, // ✅ Plaka alanı eklendi
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Abonelik başlatılamadı");
      }
      toast({
        title: "Abonelik başlatıldı",
        status: "success",
      });
      setStartSubModal({
        isOpen: false,
        device: null,
        abonelikTipi: "12 Ay",
        baslangic: todayIso,
        satisTuru: "Satılık",
        montajPersoneli: "",
        plaka: "",
      });
      loadDevices();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Araç ekleme hatası");
    }
  };

  // IMEI geçmişi modal handlers
  const handleOpenImeiGecmisi = (aracData) => {
    setSelectedAracForGecmis(aracData);
    setIsImeiGecmisiModalOpen(true);
    closeMenu();
  };

  // SIM geçmişi modal handlers
  const handleOpenSimGecmisi = (aracData) => {
    setSelectedAracForSimGecmis(aracData);
    setIsSimGecmisiModalOpen(true);
    closeMenu();
  };

  // Plaka değişikliği modal handlers
  const handleOpenPlakaDegistir = (aracData) => {
    setSelectedAracForPlaka(aracData);
    setIsPlakaModalOpen(true);
    closeMenu();
  };

  const handlePlakaSuccess = (result) => {
    loadDevices(); // Liste yenile
    setIsPlakaModalOpen(false);
    setSelectedAracForPlaka(null);
  };

  // Plaka geçmişi modal handlers
  const handleOpenPlakaGecmisi = (aracData) => {
    setSelectedAracForPlakaGecmis(aracData);
    setIsPlakaGecmisiModalOpen(true);
    closeMenu();
  };

  // Not ekle modal handlers
  const handleOpenNotEkle = (aracData) => {
    setSelectedAracForNot(aracData);
    setIsNotEkleModalOpen(true);
    closeMenu();
  };

  const handleNotEkleSuccess = () => {
    loadDevices(); // Liste yenile
    setIsNotEkleModalOpen(false);
    setSelectedAracForNot(null);
  };

  // İade al modal handlers
  const handleOpenIadeAl = (aracData) => {
    setSelectedAracForIade(aracData);
    setIsIadeAlModalOpen(true);
    closeMenu();
  };

  const handleIadeAlSuccess = () => {
    loadDevices(); // Liste yenile
    setIsIadeAlModalOpen(false);
    setSelectedAracForIade(null);
  };

  const handleOpenAksesuarIslemleri = (aracData) => {
    setSelectedAracForAksesuar(aracData);
    setIsAksesuarModalOpen(true);
    closeMenu();
  };

  const handleAksesuarSuccess = () => {
    loadDevices(false, selectedFirmId, appliedBayiFilter);
    setIsAksesuarModalOpen(false);
    setSelectedAracForAksesuar(null);
  };




  const handleDownloadPDF = async () => {
    // Listeleme yapılmadan PDF indirilemesin
    if (!hasSearched) {
      toast({
        title: "Uyarı",
        description: "Önce 'Listele' butonuna basarak araçları listeleyin.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Query parametrelerini oluştur
      const params = new URLSearchParams();
      if (selectedFirmId && selectedFirmId !== "all") {
        params.append("firma_id", selectedFirmId);
      }
      // Bayi filtresi (sadece admin ve teknik_eleman için)
      const currentBayiFilter = appliedBayiFilter || bayiFilter;
      if (currentBayiFilter && (userRole === "admin" || userRole === "teknik_eleman")) {
        // "ixpilot" seçildiğinde özel bir değer gönder (backend'de null olarak yorumlanacak)
        if (currentBayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", currentBayiFilter);
        }
      }
      if (appliedPlakaFilter?.trim()) params.append("plaka", appliedPlakaFilter.trim());
      if (appliedImeiFilter?.trim()) params.append("imei", appliedImeiFilter.trim());
      if (appliedSimNoFilter?.trim()) params.append("sim_no", appliedSimNoFilter.trim());
      if (appliedSatisTuruFilter?.trim()) params.append("satis_turu", appliedSatisTuruFilter.trim());
      if (appliedUetdsFilter === "acik") params.append("uetds", "true");
      if (appliedUetdsFilter === "kapali") params.append("uetds", "false");
      
      const queryString = params.toString();
      const url = `/api/devices/pdf${queryString ? `?${queryString}` : ""}`;
      
      // PDF'i indir - apiFetch kullanarak blob response al
      const { apiFetch } = await import("utils/api");
      const response = await apiFetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("PDF oluşturulamadı");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      
      // Dosya adı
      let filename = "araclar_raporu.pdf";
      if (appliedSelectedFirm && appliedSelectedFirm.name) {
        filename = `araclar_raporu_${appliedSelectedFirm.name.replace(/\s+/g, '_')}.pdf`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "PDF indirildi",
        description: "Araçlar raporu başarıyla indirildi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(error, "PDF indirme hatası");
    }
  };

  const handleDownloadExcel = async () => {
    if (!hasSearched) {
      toast({
        title: "Uyarı",
        description: "Önce 'Listele' butonuna basarak araçları listeleyin.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setExcelLoading(true);
      const params = new URLSearchParams();
      if (selectedFirmId && selectedFirmId !== "all") {
        params.append("firma_id", selectedFirmId);
      }
      const currentBayiFilter = appliedBayiFilter || bayiFilter;
      if (currentBayiFilter && (userRole === "admin" || userRole === "teknik_eleman")) {
        if (currentBayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", currentBayiFilter);
        }
      }
      if (appliedPlakaFilter?.trim()) params.append("plaka", appliedPlakaFilter.trim());
      if (appliedImeiFilter?.trim()) params.append("imei", appliedImeiFilter.trim());
      if (appliedSimNoFilter?.trim()) params.append("sim_no", appliedSimNoFilter.trim());
      if (appliedSatisTuruFilter?.trim()) params.append("satis_turu", appliedSatisTuruFilter.trim());
      if (appliedUetdsFilter === "acik") params.append("uetds", "true");
      if (appliedUetdsFilter === "kapali") params.append("uetds", "false");

      const queryString = params.toString();
      const url = `/api/devices/excel${queryString ? `?${queryString}` : ""}`;
      const { apiFetch } = await import("utils/api");
      const response = await apiFetch(url, { method: "GET" });
      if (!response.ok) {
        throw new Error("Excel oluşturulamadı");
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      let filename = "araclar_raporu.xlsx";
      if (appliedSelectedFirm && appliedSelectedFirm.name) {
        filename = `araclar_raporu_${appliedSelectedFirm.name.replace(/\s+/g, "_")}.xlsx`;
      }
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "Excel indirildi",
        description: "Araçlar raporu başarıyla indirildi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      handleError(error, "Excel indirme hatası");
    } finally {
      setExcelLoading(false);
    }
  };

  const handleGatherSimForDevice = async (deviceData) => {
    if (!deviceData?.id) return;
    closeMenu();
    try {
      const res = await api.post(`/api/sim-kartlari/gather/${deviceData.id}`);
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data?.detail || "SIM toplama başlatılamadı");
      }
      toast({
        title: "Başarılı",
        description: data.message || "SIM numarası toplama işlemi başlatıldı",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      // Liste yenileme kaldırıldı - SIM toplama işlemi arka planda çalışıyor
      // SIM operasyonları modalı açıksa listeyi yenilemek için event gönder
      window.dispatchEvent(new CustomEvent('simOperationStarted'));
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "SIM toplama başlatma hatası");
    }
  };

  const filterFields = [
    ...(userRole === "admin" || userRole === "teknik_eleman" ? [{
      key: "bayi",
      type: "select",
      label: "Bayi",
      placeholder: "Bayi Seçiniz",
      value: bayiFilter,
      onChange: setBayiFilter,
      options: [
        { label: "Tümü", value: "" },
        { label: "ixPilot", value: "ixpilot" },
        ...bayiler.map((bayi) => ({
          label: bayi.name,
          value: bayi.id,
        })),
      ],
      isDisabled: bayilerLoading,
    }] : []),
    {
      key: "plaka",
      type: "text",
      label: "Plaka",
      placeholder: "Plaka ara",
      value: plakaFilter,
      onChange: setPlakaFilter,
    },
    {
      key: "imei",
      type: "text",
      label: "IMEI",
      placeholder: "IMEI ara",
      value: imeiFilter,
      onChange: setImeiFilter,
    },
    {
      key: "sim_no",
      type: "text",
      label: "SIM No",
      placeholder: "SIM No ara",
      value: simNoFilter,
      onChange: setSimNoFilter,
    },
    {
      key: "satis_turu",
      type: "select",
      label: "Satış Türü",
      placeholder: "Satış Türü",
      value: satisTuruFilter,
      onChange: setSatisTuruFilter,
      options: [
        { label: "Satış", value: "Satılık" },
        { label: "Kiralama", value: "Kiralık" },
        { label: "Test", value: "Test" },
      ],
    },
    {
      key: "uetds",
      type: "select",
      label: "UETDS",
      placeholder: "UETDS",
      value: uetdsFilter,
      onChange: setUetdsFilter,
      options: [
        { label: "Tümü", value: "" },
        { label: "Açık", value: "acik" },
        { label: "Kapalı", value: "kapali" },
      ],
    },
  ];


  // Render
  return (
    <Box position="relative" mt={{ base: 2, md: 6 }}>
      <FirmSelectorWithFilters
        title="Araçlar"
        description="Firma seçildiğinde otomatik yüklenir. Filtreleri doldurup 'Listele' butonuna basarak arama yapabilirsiniz."
        firms={firms}
        selectedFirmId={selectedFirmId}
        onChange={setSelectedFirmId}
        loading={firmsLoading}
        error={firmsError}
        includeAllOption
        fields={filterFields}
        onSearch={() => loadDevices(true)} // Filtreleri uygula
        searchButtonLoading={loading}
        searchButtonLoadingText="Listeleniyor..."
        onReset={() => {
          setPlakaFilter("");
          setImeiFilter("");
          setSimNoFilter("");
          setSatisTuruFilter("");
          setUetdsFilter("");
          setBayiFilter("");
          setAppliedPlakaFilter("");
          setAppliedImeiFilter("");
          setAppliedSimNoFilter("");
          setAppliedSatisTuruFilter("");
          setAppliedUetdsFilter("");
          setAppliedBayiFilter("");
          setAppliedSelectedFirmId("");
          setHasSearched(false); // Arama durumunu sıfırla
          // Firma seçimini de sıfırla
          setSelectedFirmId("");
        }}
      />

      <DataTable
        title={
          appliedSelectedFirm
            ? `Araçlar • ${appliedSelectedFirm.name} • ${sortedData.length} araç`
            : `Araçlar • ${sortedData.length} araç`
        }
        filterBar={
          <HStack justify="flex-end" spacing={3}>
            {!(userRole === "bayi" && userBayiTipi === "yetkili_bayi") && userRole !== "teknik_eleman" && (
              <Button
                size="sm"
                leftIcon={<FiPlus />}
                colorScheme="blue"
                onClick={() => setEkleOpen(true)}
              >
                Araç Ekle
              </Button>
            )}
            <Button
              size="sm"
              leftIcon={<FiDownload />}
              colorScheme="blue"
              variant="outline"
              onClick={handleDownloadPDF}
              isLoading={loading}
              loadingText="Hazırlanıyor"
              isDisabled={!hasSearched}
            >
              PDF İndir
            </Button>
            <Button
              size="sm"
              leftIcon={<FiDownload />}
              colorScheme="green"
              variant="outline"
              onClick={handleDownloadExcel}
              isLoading={excelLoading}
              loadingText="Hazırlanıyor"
              isDisabled={!hasSearched}
            >
              Excel İndir
            </Button>
            <Button
              size="sm"
              leftIcon={<FiRefreshCw />}
              variant="ghost"
              onClick={() => loadDevices(false, selectedFirmId, appliedBayiFilter)}
              isLoading={loading}
              loadingText="Yükleniyor"
              isDisabled={!hasSearched}
            >
              Yenile
            </Button>
          </HStack>
        }
        columns={columns}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        data={sortedData}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={handleSort}
        tableFontSize="12px"
        tableRowHeight="24px"
        loading={loading}
        emptyMessage="Henüz liste yüklenmedi. Firma seçin veya filtreleri doldurup 'Listele' butonuna basarak arama yapabilirsiniz."
        hasSearched={hasSearched}
        onRowContextMenu={handleContextMenu}
      />

      {/* Sağ tık menüsü */}
      <AracContextMenu
        position={context.position}
        data={context.row}
        userRole={userRole}
        userBayiTipi={userBayiTipi}
        onClose={closeMenu}
        onOpenImeiDegistir={handleOpenImeiDegistir}
        onOpenImeiGecmisi={handleOpenImeiGecmisi}
        onOpenSimGecmisi={handleOpenSimGecmisi}
        onOpenPlakaDegistir={handleOpenPlakaDegistir}
        onOpenPlakaGecmisi={handleOpenPlakaGecmisi}
        onGatherSim={handleGatherSimForDevice}
        onOpenNotEkle={handleOpenNotEkle}
        onOpenIadeAl={handleOpenIadeAl}
        onOpenAksesuarIslemleri={handleOpenAksesuarIslemleri}
        onOpenUetdsKonumBildir={(row) => setAracUetdsKonum(row)}
        onUetdsAriza={(row) => setAracUetdsAriza(row)}
        onOpenMontajBaslat={(arac) => {
          setStartSubModal({
            isOpen: true,
            device: arac,
            abonelikTipi: "", // Seçili gelmesin, kullanıcı seçmeli
            baslangic: todayIso,
            satisTuru: "", // Seçili gelmesin, kullanıcı seçmeli
            montajPersoneli: arac?.montaj_personeli || "",
            plaka: "", // ✅ Plaka alanı başlangıçta boş
          });
        }}
      />

      {/* Montaj yapıldı / Abonelik başlat */}
      <Modal
        isOpen={startSubModal.isOpen}
        onClose={() =>
          setStartSubModal({
            isOpen: false,
            device: null,
            abonelikTipi: "", // Reset: Seçili gelmesin
            baslangic: todayIso,
            satisTuru: "", // Reset: Seçili gelmesin
            montajPersoneli: "",
            plaka: "",
          })
        }
        isCentered
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Montaj Yapıldı / Abonelik Başlat</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Araç: {startSubModal.device?.plaka || "-"} • IMEI:{" "}
                {startSubModal.device?.imei || "-"}
                {startSubModal.device?.firma_ismi && (
                  <> • Firma: {formatFirmaName(startSubModal.device.firma_ismi)}</>
                )}
              </Text>
              <FormControl>
                <FormLabel>Plaka (Opsiyonel)</FormLabel>
                <Input
                  placeholder="Plaka güncellemek için giriniz"
                  value={startSubModal.plaka}
                  onChange={(e) =>
                    setStartSubModal((p) => ({
                      ...p,
                      plaka: e.target.value.toLocaleUpperCase("tr-TR"),
                    }))
                  }
                />
                <FormHelperText fontSize="xs" color="gray.500">
                  Boş bırakılırsa plaka değişmez. Doldurulursa plaka güncellenir.
                </FormHelperText>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Abonelik Tipi</FormLabel>
                <Select
                  value={startSubModal.abonelikTipi}
                  onChange={(e) =>
                    setStartSubModal((p) => ({ ...p, abonelikTipi: e.target.value }))
                  }
                  placeholder="Seçiniz"
                >
                  <option value="1 Ay">1 Ay</option>
                  <option value="12 Ay">12 Ay</option>
                  <option value="24 Ay">24 Ay</option>
                  <option value="36 Ay">36 Ay</option>
                  <option value="Demo">Demo</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Başlangıç Tarihi</FormLabel>
                <Input
                  type="date"
                  value={startSubModal.baslangic}
                  onChange={(e) =>
                    setStartSubModal((p) => ({ ...p, baslangic: e.target.value }))
                  }
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Satış Türü</FormLabel>
                <Select
                  value={startSubModal.satisTuru}
                  onChange={(e) =>
                    setStartSubModal((p) => ({
                      ...p,
                      satisTuru: e.target.value,
                      abonelikTipi: e.target.value === "Kiralık" ? "1 Ay" : p.abonelikTipi,
                    }))
                  }
                  placeholder="Seçiniz"
                >
                  <option value="Satılık">Satış</option>
                  <option value="Kiralık">Kiralama</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Montaj Personeli</FormLabel>
                <Select
                  placeholder="Personel seçiniz"
                  value={startSubModal.montajPersoneli}
                  onChange={(e) =>
                    setStartSubModal((p) => ({ ...p, montajPersoneli: e.target.value }))
                  }
                >
                  {personelOptions.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleStartSubSave}
              isDisabled={!startSubModal.device}
            >
              Başlat
            </Button>
            <Button
              variant="ghost"
              onClick={() =>
                setStartSubModal({
                  isOpen: false,
                  device: null,
                  abonelikTipi: "12 Ay",
                  baslangic: todayIso,
                  satisTuru: "Satılık",
                  montajPersoneli: "",
                })
              }
            >
              İptal
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Araç ekle modal */}
      <AracEkleModal
        isOpen={isEkleOpen}
        onClose={() => setEkleOpen(false)}
        onSave={(data) => {
          // Yeni eklenen aracı tek başına göster
          if (data?.device?.id) {
            // Response'dan gelen device bilgisini kullanarak geçici bir device objesi oluştur
            const newDevice = {
              id: data.device.id,
              plaka: data.device.plaka,
              imei: data.device.imei,
              sim_no: null, // Henüz SIM toplanmadı
              cihaz_tipi: null, // Response'da yok
              montaj_personeli: null, // Response'da yok
              satis_tarihi: data.device.satis_tarihi,
              satis_turu: data.device.satis_turu,
              firma_ismi: data.device.firma,
              firma_id: null, // Response'da yok
              firma_telefon: null,
              firma_email: null,
              firma_yetkili_adi: null,
              abonelik_durumu: data.device.abonelik_tipi ? "aktif" : "pasif",
              son_baglanti: "bilgi yok", // Henüz Traccar'dan çekilmedi
              durum: "offline", // Varsayılan
              notlar: null,
              is_ortagi: null,
            };
            setDevices([newDevice]); // Sadece yeni araç
            setHasSearched(true); // Liste görünür olsun
          }
          setEkleOpen(false);
        }}
        bayiler={bayiler} // ✅ Bayiler verisini prop olarak geç
      />

      {/* IMEI Değişikliği Modal */}
      <ImeiDegistirModal
        isOpen={isImeiModalOpen}
        onClose={() => {
          setIsImeiModalOpen(false);
          setSelectedAracForImei(null);
        }}
        aracData={selectedAracForImei}
        onSuccess={handleImeiSuccess}
        onOpenImeiGecmisi={handleOpenImeiGecmisi}
      />

      {/* IMEI Geçmişi Modal */}
      <ImeiGecmisiModal
        isOpen={isImeiGecmisiModalOpen}
        onClose={() => {
          setIsImeiGecmisiModalOpen(false);
          setSelectedAracForGecmis(null);
        }}
        aracData={selectedAracForGecmis}
      />

      {/* SIM Geçmişi Modal */}
      <SimGecmisiModal
        isOpen={isSimGecmisiModalOpen}
        onClose={() => {
          setIsSimGecmisiModalOpen(false);
          setSelectedAracForSimGecmis(null);
        }}
        aracData={selectedAracForSimGecmis}
      />

      {/* Plaka Değişikliği Modal */}
      <PlakaDegistirModal
        isOpen={isPlakaModalOpen}
        onClose={() => {
          setIsPlakaModalOpen(false);
          setSelectedAracForPlaka(null);
        }}
        aracData={selectedAracForPlaka}
        onSuccess={handlePlakaSuccess}
        onOpenPlakaGecmisi={handleOpenPlakaGecmisi}
      />

      {/* Plaka Geçmişi Modal */}
      <PlakaGecmisModal
        isOpen={isPlakaGecmisiModalOpen}
        onClose={() => {
          setIsPlakaGecmisiModalOpen(false);
          setSelectedAracForPlakaGecmis(null);
        }}
        aracData={selectedAracForPlakaGecmis}
      />

      {/* Not Ekle Modal */}
      <NotEkleModal
        isOpen={isNotEkleModalOpen}
        onClose={() => {
          setIsNotEkleModalOpen(false);
          setSelectedAracForNot(null);
        }}
        aracData={selectedAracForNot}
        onSuccess={handleNotEkleSuccess}
      />

      <UetdsKonumBildirModal
        isOpen={Boolean(aracUetdsKonum)}
        onClose={() => setAracUetdsKonum(null)}
        arac={aracUetdsKonum}
      />

      <UetdsArizaModal
        isOpen={Boolean(aracUetdsAriza)}
        onClose={() => setAracUetdsAriza(null)}
        arac={aracUetdsAriza}
      />

      {/* İade Al Modal */}
      <IadeAlModal
        isOpen={isIadeAlModalOpen}
        onClose={() => {
          setIsIadeAlModalOpen(false);
          setSelectedAracForIade(null);
        }}
        aracData={selectedAracForIade}
        onSuccess={handleIadeAlSuccess}
      />

      <AksesuarIslemleriModal
        isOpen={isAksesuarModalOpen}
        onClose={() => {
          setIsAksesuarModalOpen(false);
          setSelectedAracForAksesuar(null);
        }}
        aracData={selectedAracForAksesuar}
        onSuccess={handleAksesuarSuccess}
      />

    </Box>
  );
}